import { useCallback, useEffect, useMemo } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { SelectFilter } from "@skydio/rivet-ui";

import { useSelector } from "hooks/redux";
import { useArraySearchParam } from "hooks/searchParams";
import { getOrgUuid } from "state/current_user/selectors";

import { useConnectionNodes } from "../Pagination";
import { getRgTitle, query, rgFragment } from "./ResourceGroupFilter";

import type { FC } from "react";
import type { ResourceGroupFilter_organization$key } from "__generated__/ResourceGroupFilter_organization.graphql";
import type { ResourceGroupFilterQuery } from "__generated__/ResourceGroupFilterQuery.graphql";
import type { BaseProps } from "./SelectFilter";

interface Props extends BaseProps {
  onSelect?: () => void;
  label: string;
  disabled?: boolean;
}

const PAGE_SIZE = 100;

const ResourceGroupFilter: FC<Props> = ({
  onSelect,
  className,
  style,
  label,
  placeholder,
  paramKey,
  disabled,
}) => {
  const isDisabled = disabled ?? false;
  const orgUuid = useSelector(getOrgUuid);
  const data = useLazyLoadQuery<ResourceGroupFilterQuery>(query, {
    orgUuid,
    count: PAGE_SIZE,
  });
  const paginationQuery = usePaginationFragment<
    ResourceGroupFilterQuery,
    ResourceGroupFilter_organization$key
  >(rgFragment, data.organization);

  useEffect(() => {
    if (paginationQuery.hasNext) {
      paginationQuery.loadNext(PAGE_SIZE);
    }
  }, [paginationQuery]);

  const paginatedResourceGroups = paginationQuery?.data?.resourceGroups;
  const rgs = useConnectionNodes(paginatedResourceGroups);

  const [selectedRgs, setSelectedRgs] = useArraySearchParam(paramKey);

  const handleSelectionChange = useCallback(
    (keys: Set<React.Key>) => {
      setSelectedRgs(Array.from(keys) as string[]);
      onSelect && onSelect();
    },
    [setSelectedRgs, onSelect]
  );

  const resourceGroupItems = useMemo(
    () => rgs.map(rg => <SelectFilter.Item key={rg.uuid}>{getRgTitle(rg)}</SelectFilter.Item>),
    [rgs]
  );

  return (
    <SelectFilter
      filterLabel={label}
      selectionMode="multiple"
      className={className}
      style={style}
      selectedKeys={selectedRgs}
      placeholder={placeholder}
      showSelectedValue={false}
      disabled={isDisabled}
      onSelectionChange={handleSelectionChange}
    >
      {resourceGroupItems}
    </SelectFilter>
  );
};

export default ResourceGroupFilter;
